import React from 'react';
import { 
  Heart, MessageCircle, Eye, TrendingUp, Flame, Award, Users, Clock, 
  ChevronUp, ChevronDown, Share2, Bookmark, Edit3, Camera, MapPin, 
  Calendar, Hash, Leaf, Activity, BarChart3, Target, Crown, 
  Coffee, Moon, Sun, Lightbulb, Palette, Music, Save, User, MessageSquare,
  Globe, Lock, BookOpen, Microscope, Beaker, Download, ArrowRight,
  Star, Trophy, Gift, Bell, Plus, X, Video, UserCircle, Zap, Sparkles
} from 'lucide-react';

const EnhancedActivityCard = ({ activity, onVote, onBookmark, onShare }) => {
  const getSmokerEmoji = (level) => {
    switch(level) {
      case 'beginner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp';
      case 'casual': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp';
      case 'connoisseur': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp';
      case 'stoner': return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp';
      default: return 'https://highmind-bucket.s3.us-east-1.amazonaws.com/6d4ba06007624c3691fd682dfbb31cdf_hmind.png';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else {
      return 'Just now';
    }
  };

  const getActivityTypeInfo = (type) => {
    switch (type) {
      case 'thread':
        return {
          icon: <MessageSquare size={14} />,
          color: '#8b5cf6',
          bgColor: '#8b5cf620',
          borderColor: '#8b5cf640',
          label: 'Thread'
        };
      case 'comment':
        return {
          icon: <MessageCircle size={14} />,
          color: '#00d4aa',
          bgColor: '#00d4aa20',
          borderColor: '#00d4aa40',
          label: 'Comment'
        };
      case 'research_article':
        return {
          icon: <BookOpen size={14} />,
          color: '#3b82f6',
          bgColor: '#3b82f620',
          borderColor: '#3b82f640',
          label: 'Research Article'
        };
      case 'article_discussion':
        return {
          icon: <Microscope size={14} />,
          color: '#f59e0b',
          bgColor: '#f59e0b20',
          borderColor: '#f59e0b40',
          label: 'Article Discussion'
        };
      case 'poll':
        return {
          icon: <BarChart3 size={14} />,
          color: '#ef4444',
          bgColor: '#ef444420',
          borderColor: '#ef444440',
          label: 'Poll'
        };
      default:
        return {
          icon: <Activity size={14} />,
          color: '#666',
          bgColor: '#66620',
          borderColor: '#66640',
          label: 'Activity'
        };
    }
  };

  const getCategoryTheme = (category) => {
    const themes = {
      medical: { 
        icon: <Microscope size={16} />, 
        gradient: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
        bgPattern: '#1e40af15'
      },
      growing: { 
        icon: <Leaf size={16} />, 
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        bgPattern: '#05966915'
      },
      strains: { 
        icon: <Beaker size={16} />, 
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
        bgPattern: '#7c3aed15'
      },
      general: { 
        icon: <BookOpen size={16} />, 
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        bgPattern: '#d9770615'
      }
    };
    return themes[category] || themes.general;
  };

  // Thread Activity Card
  const ThreadCard = () => {
    const typeInfo = getActivityTypeInfo(activity.type);
    
    return (
      <div style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        borderRadius: '12px',
        border: '1px solid #333',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = typeInfo.color;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      onClick={() => {
        console.log('Navigate to thread:', activity.thread_id);
      }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Activity Type Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: typeInfo.bgColor,
              border: `1px solid ${typeInfo.borderColor}`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ color: typeInfo.color }}>
                {typeInfo.icon}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: typeInfo.color,
                textTransform: 'capitalize'
              }}>
                {typeInfo.label}
              </span>
            </div>

            {/* Sesh Room Badge */}
            {activity.sesh_room_name && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                background: '#3b82f620',
                border: '1px solid #3b82f640',
                borderRadius: '8px',
                marginLeft: '8px',
                marginBottom: '8px'
              }}>
                <Users size={10} style={{ color: '#3b82f6' }} />
                <span style={{
                  fontSize: '10px',
                  fontWeight: '500',
                  color: '#3b82f6'
                }}>
                  {activity.sesh_room_name}
                </span>
              </div>
            )}

            <p style={{
              margin: '0 0 6px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e5e5',
              lineHeight: '1.3'
            }}>
              {activity.title}
            </p>

            {activity.content && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#bbb',
                lineHeight: '1.4',
                opacity: 0.8
              }}>
                {activity.content}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#888'
              }}>
                {formatTimeAgo(activity.created_at)}
              </span>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle upvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronUp size={12} />
                  </button>
                  
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#e5e5e5',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {activity.likes}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle downvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>

                {activity.replies > 0 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#8b5cf6'
                  }}>
                    {/* <MessageCircle size={12} />
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>
                      {activity.replies}
                    </span> */}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Research Article Activity Card
  const ResearchArticleCard = () => {
    const typeInfo = getActivityTypeInfo(activity.type);
    const category = activity.category || 'general';
    const theme = getCategoryTheme(category);
    
    return (
      <div style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        borderRadius: '12px',
        border: '1px solid #333',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = typeInfo.color;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Activity Type Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: typeInfo.bgColor,
              border: `1px solid ${typeInfo.borderColor}`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ color: typeInfo.color }}>
                {typeInfo.icon}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: typeInfo.color
              }}>
                {typeInfo.label}
              </span>
            </div>

            {/* Category Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 6px',
              background: theme.bgPattern,
              border: `1px solid ${category === 'medical' ? '#3b82f640' : 
                       category === 'growing' ? '#10b98140' :
                       category === 'strains' ? '#8b5cf640' : '#f59e0b40'}`,
              borderRadius: '8px',
              marginLeft: '8px',
              marginBottom: '8px'
            }}>
              {theme.icon}
              <span style={{
                fontSize: '10px',
                fontWeight: '500',
                color: category === 'medical' ? '#3b82f6' :
                       category === 'growing' ? '#10b981' :
                       category === 'strains' ? '#8b5cf6' : '#f59e0b',
                textTransform: 'capitalize'
              }}>
                {category}
              </span>
            </div>

            <p style={{
              margin: '0 0 6px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e5e5',
              lineHeight: '1.3'
            }}>
              {activity.title?.replace('📚 ', '') || 'Research Article'}
            </p>

            {activity.content && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#bbb',
                lineHeight: '1.4',
                opacity: 0.8
              }}>
                {activity.content}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '8px'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#888'
              }}>
                {formatTimeAgo(activity.created_at)}
              </span>

              {activity.read_time && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#888'
                }}>
                  <Clock size={10} />
                  <span style={{ fontSize: '11px' }}>
                    {activity.read_time} min read
                  </span>
                </div>
              )}

              {activity.difficulty && (
                <span style={{
                  padding: '2px 6px',
                  background: '#333',
                  color: '#888',
                  borderRadius: '6px',
                  fontSize: '10px'
                }}>
                  {activity.difficulty}
                </span>
              )}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle upvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronUp size={12} />
                  </button>
                  
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#e5e5e5',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {activity.likes}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle downvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#8b5cf6'
                }}>
                  {/* <MessageCircle size={12} />
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>
                    {activity.replies}
                  </span> */}
                </div>

                {activity.views && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#f59e0b'
                  }}>
                    <Eye size={12} />
                    <span style={{ fontSize: '12px', fontWeight: '600' }}>
                      {activity.views}
                    </span>
                  </div>
                )}
              </div>

              {activity.is_peer_reviewed && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#fbbf24'
                }}>
                  <Award size={12} />
                  <span style={{ fontSize: '10px', fontWeight: '600' }}>
                    Peer Reviewed
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Poll Activity Card
  const PollCard = () => {
    const typeInfo = getActivityTypeInfo(activity.type);
    
    return (
      <div style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        borderRadius: '12px',
        border: '1px solid #333',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = typeInfo.color;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Activity Type Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: typeInfo.bgColor,
              border: `1px solid ${typeInfo.borderColor}`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ color: typeInfo.color }}>
                {typeInfo.icon}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: typeInfo.color
              }}>
                {typeInfo.label}
              </span>
            </div>

            {/* Poll Status Badge */}
            {activity.poll_data?.is_active && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                background: '#10b98120',
                border: '1px solid #10b98140',
                borderRadius: '8px',
                marginLeft: '8px',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: '#10b981',
                  animation: 'pulse 2s infinite'
                }} />
                <span style={{
                  fontSize: '10px',
                  fontWeight: '500',
                  color: '#10b981'
                }}>
                  Active Poll
                </span>
              </div>
            )}

            <p style={{
              margin: '0 0 6px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e5e5',
              lineHeight: '1.3'
            }}>
              {activity.title?.replace('🗳️ ', '') || 'Poll'}
            </p>

            {activity.content && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#bbb',
                lineHeight: '1.4',
                opacity: 0.8
              }}>
                {activity.content}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#888'
              }}>
                {formatTimeAgo(activity.created_at)}
              </span>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle upvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronUp size={12} />
                  </button>
                  
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#e5e5e5',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {activity.likes}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle downvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#ef4444'
                }}>
                  <BarChart3 size={12} />
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>
                    {activity.poll_data?.total_votes || activity.replies} votes
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Article Discussion Activity Card
  const ArticleDiscussionCard = () => {
    const typeInfo = getActivityTypeInfo(activity.type);
    
    return (
      <div style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        borderRadius: '12px',
        border: '1px solid #333',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = typeInfo.color;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Activity Type Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: typeInfo.bgColor,
              border: `1px solid ${typeInfo.borderColor}`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ color: typeInfo.color }}>
                {typeInfo.icon}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: typeInfo.color
              }}>
                Discussion
              </span>
            </div>

            {/* Article Category Badge */}
            {activity.article_category && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 6px',
                background: '#8b5cf620',
                border: '1px solid #8b5cf640',
                borderRadius: '8px',
                marginLeft: '8px',
                marginBottom: '8px'
              }}>
                <BookOpen size={10} style={{ color: '#8b5cf6' }} />
                <span style={{
                  fontSize: '10px',
                  fontWeight: '500',
                  color: '#8b5cf6',
                  textTransform: 'capitalize'
                }}>
                  {activity.article_category}
                </span>
              </div>
            )}

            <p style={{
              margin: '0 0 6px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e5e5',
              lineHeight: '1.3'
            }}>
              {activity.title}
            </p>

            {activity.content && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#bbb',
                lineHeight: '1.4',
                opacity: 0.8
              }}>
                {activity.content}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#888'
              }}>
                {formatTimeAgo(activity.created_at)}
              </span>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle upvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronUp size={12} />
                  </button>
                  
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#e5e5e5',
                    minWidth: '20px',
                    textAlign: 'center'
                  }}>
                    {activity.likes}
                  </span>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle downvote logic here
                    }}
                    style={{
                      padding: '4px',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <ChevronDown size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Default Comment Card (for backward compatibility)
  const CommentCard = () => {
    const typeInfo = getActivityTypeInfo(activity.type);
    
    return (
      <div style={{
        padding: '12px',
        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
        borderRadius: '12px',
        border: '1px solid #333',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = typeInfo.color;
        e.currentTarget.style.transform = 'translateX(4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '#333';
        e.currentTarget.style.transform = 'translateX(0)';
      }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1 }}>
            {/* Activity Type Badge */}
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              background: typeInfo.bgColor,
              border: `1px solid ${typeInfo.borderColor}`,
              borderRadius: '12px',
              marginBottom: '8px'
            }}>
              <span style={{ color: typeInfo.color }}>
                {typeInfo.icon}
              </span>
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                color: typeInfo.color
              }}>
                Reply
              </span>
            </div>

            <p style={{
              margin: '0 0 6px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#e5e5e5',
              lineHeight: '1.3'
            }}>
              {activity.title}
            </p>

            {activity.content && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '12px',
                color: '#bbb',
                lineHeight: '1.4',
                opacity: 0.8
              }}>
                {activity.content}
              </p>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{
                fontSize: '12px',
                color: '#888'
              }}>
                {formatTimeAgo(activity.created_at)}
              </span>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#00d4aa'
                }}>
                  <Heart size={12} />
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>
                    {activity.likes}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render appropriate card based on activity type
  switch (activity.type) {
    case 'thread':
      return <ThreadCard />;
    case 'research_article':
      return <ResearchArticleCard />;
    case 'poll':
      return <PollCard />;
    case 'article_discussion':
      return <ArticleDiscussionCard />;
    case 'comment':
      return <CommentCard />;
    default:
      return <ThreadCard />; // Fallback to thread card
  }
};

// Updated RecentActivityCard component for the profile page
export const RecentActivityCard = ({ 
  recentActivity,  
  activityLoading, 
   colors,
  formatTimeAgo, 
  getActivityTypeInfo ,
 
}) => {
  return (
    <div style={{
      background: colors.bgTertiary,
      borderRadius: '20px',
      padding: '20px',
      border: '1px solid #333'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: '#e5e5e5',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Activity size={16} style={{ color: '#00d4aa' }} />
        Recent Activity
        {activityLoading && (
          <div style={{
            width: '12px',
            height: '12px',
            border: '2px solid #333',
            borderTop: '2px solid #00d4aa',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginLeft: '8px'
          }} />
        )}
      </h3>
      
      {activityLoading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {[...Array(4)].map((_, index) => (
            <div key={index} style={{
              padding: '12px',
              background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
              borderRadius: '12px',
              border: '1px solid #333',
              opacity: 0.5
            }}>
              <div style={{
                height: '16px',
                background: '#333',
                borderRadius: '4px',
                marginBottom: '8px',
                width: '80%'
              }} />
              <div style={{
                height: '12px',
                background: '#333',
                borderRadius: '4px',
                width: '60%'
              }} />
            </div>
          ))}
        </div>
      ) : recentActivity.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '32px 16px',
          color: '#888'
        }}>
          <Activity size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ margin: 0, fontSize: '14px' }}>
            No recent activity yet
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
            Start participating in discussions!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {recentActivity.map((activity, index) => (
            <EnhancedActivityCard
              key={`${activity.type}-${activity.id}-${index}`}
              activity={activity}
              onVote={(postId, direction) => {
                // Handle voting logic
                console.log('Vote:', postId, direction);
              }}
              onBookmark={(postId) => {
                // Handle bookmark logic
                console.log('Bookmark:', postId);
              }}
              onShare={(activity) => {
                // Handle share logic
                console.log('Share:', activity.title);
              }}
            />
          ))}
        </div>
      )}

      {/* View All Activity Button */}
      {recentActivity.length > 0 && (
        <div style={{
          marginTop: '16px',
          textAlign: 'center',
          padding: '12px 0',
          borderTop: '1px solid #333'
        }}>
          <button
            onClick={() => {
              // Navigate to full activity page or expand view
              console.log('View all activity');
            }}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)',
              border: '1px solid #00d4aa40',
              borderRadius: '12px',
              color: '#00d4aa',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa30 0%, #10b98130 100%)';
              e.currentTarget.style.borderColor = '#00d4aa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #00d4aa20 0%, #10b98120 100%)';
              e.currentTarget.style.borderColor = '#00d4aa40';
            }}
          >
            View All Activity →
          </button>
        </div>
      )}
    </div>
  );
};

export default EnhancedActivityCard;
